/**
 * A place for a photograph, with a plain framed box until the photo exists.
 *
 * Given a `src`, it shows that photo. Without one it is a neutral framed box
 * that says what photo belongs there, never a fake photo. By default the box
 * is for development only and draws nothing in production (the page never
 * shows an empty frame); a slot the owner wants visible while the photo is
 * pending sets `alwaysShown` (the seed-packet section's).
 *
 * Used by: sections/SeedPackets.tsx, sections/Share.tsx and
 * sections/FridayNote.tsx.
 *
 * @module PhotoSlot
 */

import React from 'react';
import { cn } from '@/lib/utils';

/** Which photo belongs here, and how the box is sized. */
interface PhotoSlotProps {
  /** What the photo shows, said in words (in the page's language). */
  label: string;
  /** Classes for the box's size and shape. */
  className?: string;
  /** The photo's address, once there is one. */
  src?: string | null;
  /** Keeps the framed box in production while there is no photo. */
  alwaysShown?: boolean;
}

/**
 * The photo, or the framed box; nothing in production unless alwaysShown.
 *
 * @param props - See PhotoSlotProps.
 */
export const PhotoSlot: React.FC<PhotoSlotProps> = ({ label, className, src, alwaysShown = false }) => {
  if (src) return <img src={src} alt={label} loading="lazy" className={cn('rounded-xl object-cover', className)} />;
  if (import.meta.env.PROD && !alwaysShown) return null;
  return (
    <div
      role="img"
      aria-label={label}
      className={cn('flex items-center justify-center rounded-xl border-2 border-dashed border-border bg-muted p-3 text-center text-sm text-muted-foreground', className)}
    >
      {label}
    </div>
  );
};
